import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import appConfig from "../../../config/appConfig";
import LijjatProductDetailsView from "../shared/LijjatProductDetailsView";

const LijjatProductVerificationPage = () => {
  const [productData, setProductData] = useState(null);
  const [locationData, setLocationData] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [, setGpsStatus] = useState("");
  const fetchedTokenRef = useRef(null);
  const { token } = useParams();

  const getAndSaveUserLocation = async (rsnId) => {
    if (!window.isSecureContext) {
      setGpsStatus("Location requires HTTPS or localhost");
      setMessage((currentMessage) =>
        currentMessage || "Location access works only on HTTPS or localhost."
      );
      return;
    }

    if (!navigator.geolocation) {
      setGpsStatus("Geolocation not supported");
      setMessage((currentMessage) =>
        currentMessage || "Geolocation is not supported in this browser."
      );
      return;
    }

    setGpsStatus("Fetching your location...");

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const lat = position.coords.latitude;
          const lon = position.coords.longitude;
          setGpsStatus("Resolving address...");

          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1`,
            { headers: { "User-Agent": "ReactLocationApp/1.0" } }
          );
          const data = await res.json();

          const area =
            data.address?.neighbourhood ||
            data.address?.suburb ||
            data.address?.village ||
            data.address?.town ||
            "Unknown";
          const city =
            data.address?.city ||
            data.address?.district ||
            data.address?.state_district ||
            data.address?.town ||
            "Unknown";
          const state = data.address?.state || "Unknown";
          const fullAddress = data.display_name || `${area}, ${city}, ${state}`;

          const saveRes = await fetch(`${appConfig.apiBaseUrl}/saveUserLocation`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              UserID: null,
              RSNID: rsnId || null,
              Latitude: lat,
              Longitude: lon,
              Area: area,
              City: city,
              State: state,
              FullAddress: fullAddress,
            }),
          });

          if (!saveRes.ok) {
            throw new Error("Failed to save");
          }

          setGpsStatus("Location saved successfully");
        } catch (error) {
          console.error("Location save error:", error);
          setGpsStatus("Failed to save location");
        }
      },
      (error) => {
        let nextMessage = "Location access denied";

        if (error.code === 1) nextMessage = "Permission denied";
        if (error.code === 2) nextMessage = "Location unavailable";
        if (error.code === 3) nextMessage = "Timeout";

        setGpsStatus(nextMessage);
        setMessage((currentMessage) => currentMessage || `Location error: ${nextMessage}.`);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  };

  useEffect(() => {
    if (token && fetchedTokenRef.current !== token) {
      fetchedTokenRef.current = token;
      const fetchData = async () => {
        setLoading(true);
        setMessage("");
        setProductData(null);
        setLocationData(null);
        setStatus(null);
        setGpsStatus("Waiting for product verification...");

        try {
          const productId = Number.parseInt(token.slice(0, 3), 10);
          const trimmedToken = token.slice(3);

          const decryptResponse = await fetch(
            `${appConfig.apiBaseUrl}/decrypt/${trimmedToken}`
          );
          const decryptResult = await decryptResponse.json();

          if (!decryptResult.success || !decryptResult.rsn) {
            setStatus("unauth");
            setMessage("Invalid or tampered code.");
            return;
          }

          const decryptedRSN = decryptResult.rsn;
          const locationId = Number.parseInt(decryptedRSN.slice(0, 3), 10);
          const dateIndex = decryptedRSN.slice(3, 9);

          const response = await fetch(`${appConfig.apiBaseUrl}/getRSNDetails`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              rsn: decryptedRSN,
              LocationID: locationId,
              DateIndex: dateIndex,
              ProductID: productId,
            }),
          });
          const result = await response.json();

          if (!result.success || !result.data) {
            setStatus("unauth");
            setMessage("This product is unauthentic.");
            return;
          }

          const data = result.data;
          setProductData({
            productName: data.ProductName,
            batchNumber: data.BatchNo,
            productCode: data.ProductCode,
            batchName: data.UnitCode,
            manufacturingDate: data.MFGDate,
            expiryDate: data.EXPDate,
            mrp: data.MRP,
          });
          setLocationData({
            branchName: data.BranchName,
            street1: data.Location?.Street1,
            street2: data.Location?.Street2,
            street3: data.Location?.Street3,
            street4: data.Location?.Street4,
            street5: data.Location?.Street5,
            street6: data.Location?.Street6,
            state: data.State,
            manufacturedBy: data.ManufacturedBy,
            customerCare: data.CustomerCare,
            fssaiNo: data.FSSAINo,
          });

          if (data.ScannedCount <= 10) {
            setStatus("auth");
          } else {
            setStatus("duplicate");
            setMessage("This product has been scanned multiple times and may be a duplicate.");
          }

          await getAndSaveUserLocation(data.RSNID);
        } catch (error) {
          console.error("Error fetching product data:", error);
          setStatus("unauth");
          setMessage("Unable to verify this product right now.");
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [token]);

  return (
    <LijjatProductDetailsView
      productData={productData}
      locationData={locationData}
      status={status}
      loading={loading}
      message={message}
    />
  );
};

export default LijjatProductVerificationPage;
